import { MapPin, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import LegalNotice from "@/components/LegalNotice";

const REGIONS = [
  {
    id: "mo",
    name: "Missouri — Kansas City Metro",
    cities: [
      "Kansas City, MO", "Independence", "Blue Springs", "Lee's Summit",
      "Raytown", "Grandview", "Belton", "Gladstone",
      "North Kansas City", "Liberty", "Kearney", "Smithville",
      "Parkville", "Platte City",
    ],
  },
  {
    id: "ne",
    name: "Nebraska — Omaha Metro",
    cities: [
      "Omaha, NE", "Bellevue", "Gretna", "Ralston",
      "Bennington", "Irvington", "Papillion", "Fremont",
    ],
  },
  {
    id: "ia",
    name: "Iowa",
    cities: ["Council Bluffs, IA"],
  },
  {
    id: "ks",
    name: "Kansas",
    cities: ["Kansas City, KS"],
  },
];

const EXPANSION = [
  "Columbia, MO", "St. Joseph, MO", "Jefferson City, MO",
  "Lawrence, KS", "Topeka, KS", "Lincoln, NE",
];

export default function ServiceAreas() {
  return (
    <div data-testid="service-areas-page">
      <section className="section bg-gradient-to-b from-slate-50 to-white">
        <div className="container-x max-w-4xl">
          <div className="eyebrow">Service areas</div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            Hometown care across <span className="brand-gradient-text">Missouri, Nebraska & Iowa.</span>
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Our caregivers live and work in the same communities our clients call home. Don't see your city? Call us — we regularly open new service areas.
          </p>
        </div>
      </section>

      <section className="pb-12">
        <div className="container-x">
          <div className="soft-card mb-8" data-testid="current-areas">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl brand-gradient flex items-center justify-center"><MapPin className="w-5 h-5 text-white" /></div>
              <h2 className="text-2xl font-heading font-semibold">Currently serving</h2>
            </div>
            <p className="mt-3 text-sm text-slate-600">Active caregivers available now across the regions below.</p>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-8">
              {REGIONS.map((r) => (
                <div key={r.id} data-testid={`region-${r.id}`}>
                  <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                    <span className="heart-dot" />
                    <h3 className="font-heading font-semibold text-slate-900">{r.name}</h3>
                    <span className="ml-auto text-xs text-slate-500">{r.cities.length} cities</span>
                  </div>
                  <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5">
                    {r.cities.map((c) => (
                      <li
                        key={c}
                        className="flex items-center gap-2 text-sm text-slate-700"
                        data-testid={`area-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        <MapPin className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="soft-card" data-testid="expansion-areas">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center"><Sparkles className="w-5 h-5 text-purple-700" /></div>
              <h2 className="text-2xl font-heading font-semibold">Coming soon</h2>
            </div>
            <p className="mt-3 text-sm text-slate-600">Planned expansion — join the waitlist and we'll reach out the moment we open.</p>
            <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {EXPANSION.map((c) => (
                <li key={c} className="flex items-center gap-2 text-sm text-slate-700 py-1.5">
                  <span className="w-2 h-2 rounded-full bg-purple-400" /> {c}
                </li>
              ))}
            </ul>
            <Link to="/contact" className="btn-outline mt-7 inline-flex" data-testid="join-waitlist-btn">Join waitlist</Link>
          </div>

          <div className="mt-8">
            <LegalNotice variant="availability" testId="service-areas-availability-notice">
              Coverage is subject to caregiver availability, staffing capacity and applicable state licensing.
              A specific city being listed does not guarantee immediate availability for every service — please
              contact us to confirm coverage for your address.
            </LegalNotice>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          <div className="rounded-[28px] overflow-hidden shadow-xl border border-slate-100">
            <iframe
              title="Service Area Map — KC, Omaha, Council Bluffs"
              width="100%" height="460" loading="lazy" allowFullScreen
              src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d1597830.0!2d-96.2!3d40.6!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1700000000000"
              data-testid="service-area-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
